import type { BlockchainNetwork } from "@/interfaces/blockchainNetwork";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  BadgeCheck,
  Boxes,
  Coins,
  ExternalLink,
  FileText,
  Globe,
  Hash,
  Layers3,
  MoreVertical,
  Pencil,
  RadioTower,
  Settings2,
  Trash2,
  Wallet,
  Eye,
  Ban,
  CheckCircle2,
} from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { Button } from "@/components/ui/button";
import NetworkStatusBadge from "./NetworkStatusBadge";

interface Props {
  network: BlockchainNetwork;
  onView?: (network: BlockchainNetwork) => void;
  onEdit?: (network: BlockchainNetwork) => void;
  onRpc?: (network: BlockchainNetwork) => void;
  onDelete?: (network: BlockchainNetwork) => void;
  onToggleEnabled?: (network: BlockchainNetwork) => void;
  onToggleDeposits?: (network: BlockchainNetwork) => void;
  onToggleWithdrawals?: (network: BlockchainNetwork) => void;
}

const BlockchainNetworkCard = ({
  network,
  onView,
  onEdit,
  onRpc,
  onDelete,
  onToggleEnabled,
  onToggleDeposits,
  onToggleWithdrawals,
}: Props) => {
  return (
    <div className="overflow-hidden rounded-2xl border bg-card transition-all duration-300 hover:-translate-y-1 hover:shadow-xl p-4">
      {/* Header */}

      <div className="border-b px-6 py-5">
        <div className="flex items-start justify-between">
          <div className="flex gap-4">
            <img
              src={network.icon ?? "/placeholder.png"}
              alt={network.name}
              className="h-14 w-14 rounded-xl border bg-background object-contain p-2"
            />

            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold">{network.name}</h3>

                {network.enabled ? (
                  <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                    Active
                  </span>
                ) : (
                  <span className="rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-700 dark:bg-red-900/30 dark:text-red-400">
                    Disabled
                  </span>
                )}
              </div>

              <div className="mt-2 flex flex-wrap gap-2">
                <span className="rounded-md bg-primary/10 px-2 py-1 text-xs font-semibold text-primary">
                  {network.code}
                </span>

                <span className="rounded-md border px-2 py-1 text-xs">
                  {network.networkType}
                </span>

                {network.isEvm && (
                  <span className="rounded-md border px-2 py-1 text-xs">
                    EVM
                  </span>
                )}

                {network.isUtxo && (
                  <span className="rounded-md border px-2 py-1 text-xs">
                    UTXO
                  </span>
                )}
              </div>
            </div>
          </div>

          <button className="rounded-lg p-2 transition hover:bg-muted">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreVertical className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuItem onClick={() => onView?.(network)}>
                  <Eye className="mr-2 h-4 w-4" />
                  View Details
                </DropdownMenuItem>

                <DropdownMenuItem onClick={() => onEdit?.(network)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit Network
                </DropdownMenuItem>

                <DropdownMenuItem onClick={() => onRpc?.(network)}>
                  <Settings2 className="mr-2 h-4 w-4" />
                  Configure RPC
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                <DropdownMenuItem onClick={() => onToggleEnabled?.(network)}>
                  {network.enabled ? (
                    <>
                      <Ban className="mr-2 h-4 w-4" />
                      Disable Network
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                      Enable Network
                    </>
                  )}
                </DropdownMenuItem>

                <DropdownMenuItem onClick={() => onToggleDeposits?.(network)}>
                  <ArrowDownToLine className="mr-2 h-4 w-4" />

                  {network.depositEnabled
                    ? "Disable Deposits"
                    : "Enable Deposits"}
                </DropdownMenuItem>

                <DropdownMenuItem
                  onClick={() => onToggleWithdrawals?.(network)}
                >
                  <ArrowUpFromLine className="mr-2 h-4 w-4" />

                  {network.withdrawEnabled
                    ? "Disable Withdrawals"
                    : "Enable Withdrawals"}
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                <DropdownMenuItem
                  className="text-destructive"
                  onClick={() => onDelete?.(network)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </button>
        </div>
      </div>

      {/* Content */}

      <div className="grid grid-cols-2 gap-x-8 gap-y-5">
        <div className="flex items-center gap-3">
          <Hash className="h-4 w-4 text-muted-foreground" />

          <div>
            <p className="text-xs text-muted-foreground">Chain ID</p>

            <p className="font-medium">{network.chainId ?? "-"}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <BadgeCheck className="h-4 w-4 text-muted-foreground" />

          <div>
            <p className="text-xs text-muted-foreground">Confirmations</p>

            <p className="font-medium">{network.confirmations}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Layers3 className="h-4 w-4 text-muted-foreground" />

          <div>
            <p className="text-xs text-muted-foreground">Decimals</p>

            <p className="font-medium">{network.decimals}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Boxes className="h-4 w-4 text-muted-foreground" />

          <div>
            <p className="text-xs text-muted-foreground">Assets</p>

            <p className="font-medium">{network._count.assets}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Wallet className="h-4 w-4 text-muted-foreground" />

          <div>
            <p className="text-xs text-muted-foreground">Wallets</p>

            <p className="font-medium">{network._count.addresses}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Boxes className="h-4 w-4 text-muted-foreground" />

          <div>
            <p className="text-xs text-muted-foreground">Tokens</p>

            <p className="font-medium">{network._count.tokens}</p>
          </div>
        </div>
      </div>

      <div className="my-6 border-t" />

      <div>
        <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Network Capabilities
        </p>

        <div className="flex flex-wrap gap-2">
          <NetworkStatusBadge
            active={network.depositEnabled}
            label="Deposits"
          />

          <NetworkStatusBadge
            active={network.withdrawEnabled}
            label="Withdrawals"
          />

          <NetworkStatusBadge active={network.supportsTokens} label="Tokens" />

          <NetworkStatusBadge active={network.supportsMemo} label="Memo" />

          <NetworkStatusBadge
            active={network.supportsTag}
            label="Destination Tag"
          />
        </div>
      </div>

      <div className="my-6 border-t" />

      <p className="mb-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        Infrastructure
      </p>

      <div className="space-y-4">
        <div className="flex items-center justify-between rounded-xl border p-3">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2">
              <RadioTower className="h-4 w-4 text-primary" />
            </div>

            <div>
              <p className="text-sm font-medium">RPC Endpoint</p>

              <p className="text-xs text-muted-foreground">JSON RPC</p>
            </div>
          </div>

          <NetworkStatusBadge
            active={!!network.rpcUrl}
            label={network.rpcUrl ? "Configured" : "Missing"}
          />
        </div>

        <div className="flex items-center justify-between rounded-xl border p-3">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2">
              <RadioTower className="h-4 w-4 text-primary" />
            </div>

            <div>
              <p className="text-sm font-medium">WebSocket</p>

              <p className="text-xs text-muted-foreground">Event Listener</p>
            </div>
          </div>

          <NetworkStatusBadge
            active={!!network.wsUrl}
            label={network.wsUrl ? "Configured" : "Missing"}
          />
        </div>

        <div className="flex items-center justify-between rounded-xl border p-3">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2">
              <Globe className="h-4 w-4 text-primary" />
            </div>

            <div>
              <p className="text-sm font-medium">Blockchain Explorer</p>

              <p className="text-xs text-muted-foreground">Public Explorer</p>
            </div>
          </div>

          {network.explorerUrl ? (
            <Button variant="ghost" size="sm" asChild>
              <a href={network.explorerUrl} target="_blank" rel="noreferrer">
                Open
                <ExternalLink className="ml-2 h-3 w-3" />
              </a>
            </Button>
          ) : (
            <NetworkStatusBadge active={false} label="Unavailable" />
          )}
        </div>
      </div>

      <div className="mt-6 border-t pt-5">
        <div className="grid grid-cols-3 gap-3">
          <Button variant="outline" onClick={() => onView?.(network)}>
            <Eye className="mr-2 h-4 w-4" />
            View
          </Button>

          <Button variant="outline" onClick={() => onEdit?.(network)}>
            <Pencil className="mr-2 h-4 w-4" />
            Edit
          </Button>

          <Button onClick={() => onRpc?.(network)}>
            <Settings2 className="mr-2 h-4 w-4" />
            RPC
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BlockchainNetworkCard;
